import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Mail, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AuthShell, AuthCard } from '../components/Common/AuthShell';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  // Guard against re-running when i18n finishes loading (`t` identity
  // changes) — a second /email/verify call returns "already verified"
  // and flips success → error, thrashing the DOM (and surfacing
  // removeChild NotFoundError when browser extensions wrap text nodes).
  const verifiedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage(t('verifyEmail.errorMissingToken'));
      return;
    }

    if (verifiedTokenRef.current === token) return;
    verifiedTokenRef.current = token;

    let cancelled = false;
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_URL}/email/verify?token=${encodeURIComponent(token)}`);
        const data = await response.json();

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(data.error || t('verifyEmail.errorVerificationFailed'));
        }

        setStatus('success');
        setMessage(data.message || t('verifyEmail.successDefault'));
        toast.success(t('verifyEmail.successToast'));

        redirectTimer = setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } catch (error: unknown) {
        if (cancelled) return;
        const errMessage =
          error instanceof Error
            ? error.message
            : t('verifyEmail.errorVerifyFailed');
        setStatus('error');
        setMessage(errMessage);
        toast.error(errMessage);
      }
    };

    void verifyToken();

    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
    // Intentionally omit `t` — re-verify on language load would race
    // the one-time token endpoint. Token + navigate are the only deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  return (
    <AuthShell hideFooter>
      <AuthCard>
        {/*
          Stable outer shell + keyed status blocks so React remounts a
          whole subtree on status change instead of surgically swapping
          text nodes that password managers / translators may have wrapped.
        */}
        <div className="text-center" key={status}>
          <div className="mb-6 flex justify-center">
            {status === 'loading' && (
              <div className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-cyan-300 to-violet-500 shadow-glow">
                <Loader className="h-10 w-10 animate-spin text-growth-ink" />
              </div>
            )}
            {status === 'success' && (
              <div className="grid h-20 w-20 animate-bounce place-items-center rounded-full bg-gradient-to-br from-lime-300 to-emerald-400 shadow-glow">
                <CheckCircle className="h-10 w-10 text-growth-ink" />
              </div>
            )}
            {status === 'error' && (
              <div className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-rose-400 to-red-500 shadow-glow">
                <XCircle className="h-10 w-10 text-white" />
              </div>
            )}
          </div>

          <h1 className="mb-4 text-3xl font-black">
            {status === 'loading' && <span>{t('verifyEmail.loadingTitle')}</span>}
            {status === 'success' && (
              <span className="landing-gradient-text">{t('verifyEmail.successTitle')}</span>
            )}
            {status === 'error' && (
              <span className="text-rose-400">{t('verifyEmail.errorTitle')}</span>
            )}
          </h1>

          <p className="mb-8 text-white/[0.6]">
            {status === 'loading' && <span>{t('verifyEmail.loadingDescription')}</span>}
            {status === 'success' && (
              <span>
                {message}
                <br />
                <span className="text-sm">{t('verifyEmail.successRedirecting')}</span>
              </span>
            )}
            {status === 'error' && (
              <span>
                {message}
                <br />
                <span className="text-sm">{t('verifyEmail.errorLinkExpired')}</span>
              </span>
            )}
          </p>

          {status === 'success' && (
            <button type="button" onClick={() => navigate('/dashboard')} className="landing-primary-cta w-full">
              <span>{t('verifyEmail.goToDashboard')}</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <button type="button" onClick={() => navigate('/dashboard')} className="landing-primary-cta w-full">
                <Mail className="h-4 w-4" />
                <span>{t('verifyEmail.requestNewEmail')}</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex w-full items-center justify-center rounded-2xl border border-white/14 bg-white/[0.06] px-4 py-3 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.1]"
              >
                <span>{t('common.backToHome')}</span>
              </button>
            </div>
          )}
        </div>
      </AuthCard>
    </AuthShell>
  );
};
